import { ScoreState } from "lib/redux/scoringSlice";
import { MAX_SECTIONAL_SCORE, OVERALL_ANALYSIS_SCORE, TOTAL_SCORE } from "./config";
import { ResumeAnalysis } from "./resume-analysis/resume-analyse-types";

export type ExperienceLevel = 'FRESHER' | 'EXPERIENCED';

export const calculateMaxScores = (resume: ResumeAnalysis, experienceLevel: ExperienceLevel = 'EXPERIENCED'): ScoreState => {

  const maxTotalScore = TOTAL_SCORE;
  let maxOverAllScore = 0; // 10% of maxTotalScore
  const maxSectionalScore = MAX_SECTIONAL_SCORE * TOTAL_SCORE; // 90% of maxTotalScore

  // Initialize scores
  let maxNonCoreSectionScore = 0;
  let maxCoreSectionScore = 0;

  const nonCoreSections = {
    certificatesMaxScore: 0,
    awardsMaxScore: 0,
    languagesMaxScore: 0,
    customSectionMaxScore: 0,
  };

  const coreSections = {
    projectsMaxScore: 0,
    experienceMaxScore: 0,
    educationMaxScore: 0,
    skillsMaxScore: 0,
    personalDetailsMaxScore: 0,
  };

  const hasProjects = resume.projects?.individualProjectsAnalysis?.length > 0;
  const hasExperience = resume.workExperiences?.individualWorkExperiencesAnalysis?.length > 0;
  const hasCertifications = resume.certifications?.individualCertificatesAnalysis?.length > 0;
  const hasAwards = resume.awards?.individualAwardsAnalysis?.length > 0;
  const hasLanguages = resume.languages?.individualLanguagesAnalysis?.length > 0;
  const hasCustomSection = resume.custom.descriptionAnalysis.length > 0 ? true : false;
  // const hasProfile = Boolean(
  //   resume.profile && (
  //     (resume.profile.name && String(resume.profile.name).trim()) ||
  //     (resume.profile.email && String(resume.profile.email).trim()) ||
  //     (resume.profile.phone && String(resume.profile.phone).trim()) ||
  //     (resume.profile.summaryAnalysis && resume.profile.summaryAnalysis.statement && String(resume.profile.summaryAnalysis.statement).trim()) ||
  //     (resume.profile.location && String(resume.profile.location).trim())
  //   )
  // );
  const numberOfCoreSections = 0 + (hasCertifications ? 1 : 0)
    + (hasAwards ? 1 : 0)
    + (hasLanguages ? 1 : 0)
    + (hasCustomSection ? 1 : 0)

  if (experienceLevel === "FRESHER") {
    // Base allocation for FRESHER
    coreSections.personalDetailsMaxScore = 10;
    coreSections.educationMaxScore = 10;
    coreSections.skillsMaxScore = 5;
    coreSections.experienceMaxScore = 0; // Will be set based on presence
    coreSections.projectsMaxScore = 0; // Will be set based on presence
    maxOverAllScore = 25;

    // Projects logic: required only if no experience
    if (hasProjects) {
      coreSections.projectsMaxScore = 30;
      if (hasExperience) {
        coreSections.experienceMaxScore = 20;
      } else {
        // Has projects but no experience: give 10 to education and 10 to projects
        coreSections.educationMaxScore += 10; // 20
        coreSections.projectsMaxScore += 10; // 40
      }
    } else {
      // No projects
      if (hasExperience) {
        // Has experience, no projects: distribute project's 30 equally (10 each to exp, personal, education)
        coreSections.experienceMaxScore = 20 + 10; // 30
        coreSections.personalDetailsMaxScore += 10; // 20
        coreSections.educationMaxScore += 10; // 20
      } else {
        // No projects, no experience: keep projects at 30 and distribute experience points
        coreSections.projectsMaxScore = 30;
        coreSections.educationMaxScore += 10; // 20
        coreSections.personalDetailsMaxScore += 5; // 15
        coreSections.skillsMaxScore += 5; // 10
      }
    }

    // Handle non-core sections
    if (numberOfCoreSections > 0) {
      maxOverAllScore -= 10;
      const marksForEachNonCoreSection = 10 / numberOfCoreSections;
      if (hasCertifications) {
        nonCoreSections.certificatesMaxScore = marksForEachNonCoreSection;
        maxNonCoreSectionScore += marksForEachNonCoreSection;
      }
      if (hasAwards) {
        nonCoreSections.awardsMaxScore = marksForEachNonCoreSection;
        maxNonCoreSectionScore += marksForEachNonCoreSection;
      }
      if (hasLanguages) {
        nonCoreSections.languagesMaxScore = marksForEachNonCoreSection;
        maxNonCoreSectionScore += marksForEachNonCoreSection;
      }
      if (hasCustomSection) {
        nonCoreSections.customSectionMaxScore = marksForEachNonCoreSection;
        maxNonCoreSectionScore += marksForEachNonCoreSection;
      }
    }
  }
  else {
    // Base allocation for EXPERIENCED
    coreSections.personalDetailsMaxScore = 10;
    coreSections.educationMaxScore = 5;
    coreSections.skillsMaxScore = 5;
    coreSections.experienceMaxScore = 35;
    coreSections.projectsMaxScore = 0; // Will be set based on presence
    maxOverAllScore = 25;

    // Projects logic for EXPERIENCED
    if (hasProjects) {
      coreSections.projectsMaxScore = 20;
    } else {
      // No projects: distribute project's 20 points (10 to experience, 10 to education)
      coreSections.experienceMaxScore += 10; // 45
      coreSections.educationMaxScore += 10; // 15
    }

    // Handle non-core sections
    if (numberOfCoreSections > 0) {
      maxOverAllScore -= 10;
      const marksForEachNonCoreSection = 10 / numberOfCoreSections;
      if (hasCertifications) {
        nonCoreSections.certificatesMaxScore = marksForEachNonCoreSection;
        maxNonCoreSectionScore += marksForEachNonCoreSection;
      }
      if (hasAwards) {
        nonCoreSections.awardsMaxScore = marksForEachNonCoreSection;
        maxNonCoreSectionScore += marksForEachNonCoreSection;
      }
      if (hasLanguages) {
        nonCoreSections.languagesMaxScore = marksForEachNonCoreSection;
        maxNonCoreSectionScore += marksForEachNonCoreSection;
      }
      if (hasCustomSection) {
        nonCoreSections.customSectionMaxScore = marksForEachNonCoreSection;
        maxNonCoreSectionScore += marksForEachNonCoreSection;
      }
    }
  }


  return {
    maxTotalScore,
    maxOverAllScore,
    maxSectionalScore,
    maxCoreSectionScore,
    maxNonCoreSectionScore,
    coreSections,
    nonCoreSections,
  };
};
